import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { createPortal } from 'react-dom'
import mlIcon from '../../assets/icons/ML.svg'
import { OpenIDContext } from '../../api/openid'
import { WebSocketContext } from '../../api/websocket'
import {
	useUserCustomisation,
	useSaveUserCustomisation,
	useLearningPath,
	useRequestLearningPath,
	useChallengeUrl,
	useUserLearningPath,
} from '../../api/hooks'
import Tooltip from '../../shared/Tooltip/Tooltip'
import SecDimAlert from '../../shared/SecDimAlert/SecDimAlert'
import DrSecDimPanel, { PanelMode } from './DrSecDimPanel'
import DrSecDimTutorial, {
	type DrSecDimTutorialHandle,
} from './DrSecDimTutorial'
import {
	resolveMentorViewState,
	fromBackendCustomisation,
	toBackendCustomisation,
	fromBackendNodes,
	hasCustomisation,
} from './drSecDimConfig'
import * as Styled from './DrSecDimStyles'

type LearningPathStatusConfig =
	| { message: string; progress: number }
	| { messages: readonly string[]; progress: number }

// Keep refresh-state messaging aligned with the worker's started-phase
// websocket messages in learn-worker/worker/helpers.py.
const LEARNING_PATH_STATUS_CONFIG = {
	PENDING: { message: 'Preparing your learning path', progress: 0 },
	ANALYSING_LEARNING_PATH: {
		message: 'Retrieving your current learning path',
		progress: 5,
	},
	ANALYSING_USER_CUSTOMISATION: {
		message: 'Retrieving your learning preferences',
		progress: 10,
	},
	RETRIEVING_LEARNING_PATH_DETAILS: {
		message: 'Gathering details for your learning path',
		progress: 30,
	},
	GENERATING_LEARNING_PATH: {
		progress: 90,
		messages: [
			'Reviewing your preferences',
			'Reviewing your recent activity',
			'Searching catalogue for candidate options',
			'Assessing learning gaps',
			'Selecting relevant security topics',
			'Organising learning sequence',
		],
	},
	UPDATING_LEARNING_PATH: {
		message: 'Applying your learning path update.',
		progress: 100,
	},
} satisfies Partial<
	Record<LearningPathGenerationStatus, LearningPathStatusConfig>
>

const IN_FLIGHT_LEARNING_PATH_STATUSES = Object.keys(
	LEARNING_PATH_STATUS_CONFIG,
) as LearningPathGenerationStatus[]

const LLM_GENERATION_MESSAGES =
	LEARNING_PATH_STATUS_CONFIG.GENERATING_LEARNING_PATH.messages

const LLM_GENERATION_STEP = 'get_learning_path_update'

const REGENERATE_ERROR_FALLBACK =
	'Something went wrong requesting your learning path. Please try again.'

// Prefer the backend's own detail message (e.g. "already in progress") when
// available, falling back to a generic message for network/unexpected errors.
// InvalidReplaceTypeUuidError responds with {"uuid": [...]} instead of
// {"detail": ...}, so that field needs checking too.
const getRegenerateErrorMessage = (error: unknown): string =>
	(error as any)?.response?.data?.detail ??
	(error as any)?.response?.data?.uuid?.[0] ??
	REGENERATE_ERROR_FALLBACK

const PROGRESS_STEP_TO_STATUS: Record<string, LearningPathGenerationStatus> = {
	current_learning_path: 'ANALYSING_LEARNING_PATH',
	user_customisation: 'ANALYSING_USER_CUSTOMISATION',
	enrich_learning_path: 'RETRIEVING_LEARNING_PATH_DETAILS',
	get_learning_path_update: 'GENERATING_LEARNING_PATH',
	apply_learning_path_update: 'UPDATING_LEARNING_PATH',
}

// An in-flight status older than this is considered stale (e.g. the worker
// crashed) and will not show the generating UI or block a new request.
const IN_FLIGHT_STALENESS_MS = 5 * 60 * 1000

const SHORT_PHASE_DURATION_MS = 5_000
const LLM_ESTIMATED_DURATION_MS = 150_000
const LLM_PROGRESS_START = 30
const LLM_PROGRESS_CEILING = 89

function useLearningPathGenerationProgress(
	status: LearningPathGenerationStatus | undefined,
	llmGenerationStartedAt: number | null,
): number {
	const [progress, setProgress] = useState(0)
	const progressRef = useRef(0)
	const prevStatusRef = useRef<LearningPathGenerationStatus | undefined>(
		undefined,
	)

	// Terminal statuses (no status at all counts as one, e.g. just reset
	// after a completion) apply instantly, during render rather than via the
	// effect below — deferring this to an effect would paint one frame with
	// the previous run's stale progress value before snapping to the new
	// one, i.e. the next regeneration briefly flashing the last run's 100%
	// before dropping to 0.
	if (
		status !== prevStatusRef.current &&
		(!status || status === 'PENDING' || status === 'ACTIVE')
	) {
		const terminalProgress = status === 'ACTIVE' ? 100 : 0
		progressRef.current = terminalProgress
		if (progress !== terminalProgress) setProgress(terminalProgress)
	}
	prevStatusRef.current = status

	useEffect(() => {
		if (!status || status === 'PENDING' || status === 'ACTIVE') return undefined

		const target =
			LEARNING_PATH_STATUS_CONFIG[
				status as keyof typeof LEARNING_PATH_STATUS_CONFIG
			]?.progress
		if (target === undefined) return undefined

		const start =
			status === 'GENERATING_LEARNING_PATH'
				? Math.max(progressRef.current, LLM_PROGRESS_START)
				: Math.min(progressRef.current, target)
		const ceiling =
			status === 'GENERATING_LEARNING_PATH' ? LLM_PROGRESS_CEILING : target
		const animationStartedAt = performance.now()
		const llmStartedAt = llmGenerationStartedAt ?? Date.now()
		let animationFrame: number

		const tick = (now: number) => {
			// Use elapsed time to ease LLM progress exponentially toward its ceiling,
			// while short phases advance linearly and never exceed their target.
			const elapsed =
				status === 'GENERATING_LEARNING_PATH'
					? Math.max(0, Date.now() - llmStartedAt)
					: now - animationStartedAt
			const next =
				status === 'GENERATING_LEARNING_PATH'
					? start +
						(ceiling - start) *
							(1 - Math.exp((-2.2 * elapsed) / LLM_ESTIMATED_DURATION_MS))
					: start + (target - start) * Math.min(elapsed / SHORT_PHASE_DURATION_MS, 1)

			progressRef.current = Math.min(next, ceiling)
			setProgress(progressRef.current)

			if (
				status === 'GENERATING_LEARNING_PATH' ||
				elapsed < SHORT_PHASE_DURATION_MS
			) {
				animationFrame = window.requestAnimationFrame(tick)
			}
		}

		animationFrame = window.requestAnimationFrame(tick)
		return () => window.cancelAnimationFrame(animationFrame)
	}, [status, llmGenerationStartedAt])

	return progress
}

const DrSecDim = () => {
	const { authenticated, profile } = useContext(OpenIDContext)
	const { socket } = useContext(WebSocketContext)
	const canRegenerate =
		Boolean(profile.isSubscribed) &&
		Boolean(profile.restrictions.hasDrSecDimAccess)

	const [isOpen, setIsOpen] = useState(false)
	const [mode, setMode] = useState<PanelMode>('view')
	// True while the worker is generating a new learning path (POST accepted,
	// waiting for the WS completion message).
	const [isGenerationRequested, setIsGenerationRequested] = useState(false)
	// Timestamp of the most recent dispatch that set isGenerationRequested,
	// used by the staleness fallback below. Deliberately a local timestamp
	// rather than the query cache's updated_at — right after dispatch that
	// cache can still briefly reflect a pre-dispatch (and possibly already
	// old) snapshot, which would otherwise read as instantly stale.
	const generationDispatchedAtRef = useRef<number | null>(null)
	// Live progress message from the WebSocket.
	const [generatingMessage, setGeneratingMessage] = useState<string | null>(null)
	const [progressStep, setProgressStep] = useState<string | null>(null)
	const [progressStatus, setProgressStatus] =
		useState<LearningPathGenerationStatus | null>(null)
	const [llmGenerationStartedAt, setLlmGenerationStartedAt] = useState<
		number | null
	>(null)
	const [llmGenerationMessageIndex, setLlmGenerationMessageIndex] = useState(0)
	// Nodes the learner has picked in the stepper to regenerate individually,
	// as an alternative to the "regenerate everything" flow below.
	const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set())
	// Only failures surface an alert now — a successful request/completion is
	// communicated by the generating panel itself, not a toast.
	const [regenerateAlert, setRegenerateAlert] = useState<{
		open: boolean
		message: string
		severity: 'error'
	}>({ open: false, message: '', severity: 'error' })
	const containerRef = useRef<HTMLDivElement | null>(null)
	const panelRef = useRef<HTMLDivElement | null>(null)
	const tutorialActiveRef = useRef(false)
	const handleTutorialActiveChange = useCallback((active: boolean) => {
		tutorialActiveRef.current = active
	}, [])
	const setPanelOpenForTutorial = useCallback((open: boolean) => {
		setIsOpen(open)
	}, [])
	const tutorialRef = useRef<DrSecDimTutorialHandle>(null)
	const handleRestartTutorial = useCallback(() => {
		tutorialRef.current?.restart()
	}, [])
	const customisationQuery = useUserCustomisation(Boolean(authenticated))
	const saveCustomisation = useSaveUserCustomisation()
	const requestLearningPath = useRequestLearningPath()
	const userLearningPathQuery = useUserLearningPath(Boolean(authenticated))
	const learningPathStatus = userLearningPathQuery.data?.data.status
	const learningPathUpdatedAt = userLearningPathQuery.data?.data.updated_at
	const isGenerationStale = learningPathUpdatedAt
		? Date.now() - Date.parse(learningPathUpdatedAt) >= IN_FLIGHT_STALENESS_MS
		: false
	const isPersistedGenerationInFlight = Boolean(
		learningPathStatus &&
		IN_FLIGHT_LEARNING_PATH_STATUSES.includes(learningPathStatus) &&
		!isGenerationStale,
	)
	const effectiveProgressStatus = progressStatus ?? learningPathStatus
	const persistedLlmGenerationStartedAt =
		learningPathStatus === 'GENERATING_LEARNING_PATH'
			? Date.parse(userLearningPathQuery.data?.data.updated_at ?? '')
			: Number.NaN
	const generationProgress = useLearningPathGenerationProgress(
		effectiveProgressStatus,
		llmGenerationStartedAt ??
			(Number.isNaN(persistedLlmGenerationStartedAt)
				? null
				: persistedLlmGenerationStartedAt),
	)
	const persistedStatusConfig = learningPathStatus
		? LEARNING_PATH_STATUS_CONFIG[
				learningPathStatus as keyof typeof LEARNING_PATH_STATUS_CONFIG
			]
		: undefined
	const persistedGeneratingMessage =
		persistedStatusConfig && 'message' in persistedStatusConfig
			? persistedStatusConfig.message
			: null
	const isLlmGenerationStep =
		progressStep === LLM_GENERATION_STEP ||
		(progressStep === null && learningPathStatus === 'GENERATING_LEARNING_PATH')
	const displayedGeneratingMessage = isLlmGenerationStep
		? LLM_GENERATION_MESSAGES[llmGenerationMessageIndex]
		: (generatingMessage ?? persistedGeneratingMessage)
	// A customisation still controls whether setup is shown, but not whether
	// learning-path nodes may be loaded. Nodes are fetched regardless of
	// generation status: a background regeneration (e.g. triggered by
	// completing a topic, challenge, or course) shouldn't hide a path the
	// user can already act on.
	const hasExistingCustomisation = Boolean(customisationQuery.data?.data)
	const learningPathQuery = useLearningPath(Boolean(authenticated))
	// Treat 404 responses as "no data yet" rather than errors. A 404 on
	// /user-customisation means the user hasn't saved one; a 404 on
	// /learning-path means no nodes have been generated yet.
	const is404 = (error: unknown): boolean =>
		(error as any)?.response?.status === 404
	const customisationIs404 =
		customisationQuery.isError && is404(customisationQuery.error)
	const learningPathIs404 =
		learningPathQuery.isError && is404(learningPathQuery.error)
	const userLearningPathIs404 =
		userLearningPathQuery.isError && is404(userLearningPathQuery.error)
	const isError =
		(customisationQuery.isError && !customisationIs404) ||
		(learningPathQuery.isError && !learningPathIs404) ||
		(userLearningPathQuery.isError && !userLearningPathIs404)
	// Transform backend shapes into the frontend contracts.
	const backendCustomisation = customisationQuery.data?.data ?? null
	const customisation: UserCustomisation | null = backendCustomisation
		? fromBackendCustomisation(backendCustomisation)
		: null
	const backendNodes = useMemo(
		() => learningPathQuery.data?.data ?? [],
		[learningPathQuery.data],
	)
	const pathNodes = useMemo(() => fromBackendNodes(backendNodes), [backendNodes])
	const selectedTypeUuids = useMemo(
		() =>
			pathNodes
				.filter(node => selectedNodeIds.has(node.id))
				.map(node => node.typeUuid),
		[pathNodes, selectedNodeIds],
	)
	// Only force the full-screen generating view for a background
	// regeneration (isPersistedGenerationInFlight) when there aren't already
	// enough upcoming nodes to show instead. A generation the user explicitly
	// requested (isGenerationRequested) — with or without a full clear —
	// always shows it.
	const uncompletedNodeCount = pathNodes.filter(
		node => node.status !== 'completed',
	).length
	const hasEnoughVisiblePath = uncompletedNodeCount >= 2
	const isGenerating =
		isGenerationRequested ||
		(isPersistedGenerationInFlight && !hasEnoughVisiblePath)
	const currentIndex = pathNodes.findIndex(node => node.status === 'current')
	const currentNode = currentIndex >= 0 ? pathNodes[currentIndex] : undefined
	const currentBackendNode =
		currentIndex >= 0 ? backendNodes[currentIndex] : undefined
	// Challenge nodes need game-slug resolution to build the Play URL.
	const currentIsChallenge = currentBackendNode?.type === 'Challenge'
	const { data: challengeUrl } = useChallengeUrl(
		currentIsChallenge ? currentBackendNode?.type_uuid : undefined,
		Boolean(authenticated) && currentIsChallenge,
	)
	// Build the recommendation from the active path node. The backend supplies
	// the node's justification; its title has been resolved from the owning
	// Learn or Play resource by useLearningPath.
	const snapshot = useMemo<MentorSnapshot | null>(
		() =>
			pathNodes.length > 0
				? {
						name: 'Dr. SecDim',
						title: 'Your Security Coach',
						path: pathNodes,
						recommendation: currentNode?.justification
							? {
									title: currentNode.title,
									justification: currentNode.justification,
									ctaLabel: 'Go',
									ctaHref: challengeUrl ?? currentNode.href ?? '/',
								}
							: undefined,
					}
				: null,
		[pathNodes, currentNode, challengeUrl],
	)
	// While generation is in-flight the panel renders a progress message
	// instead of the view-state switch, so isGenerating is excluded from
	// isLoading here.
	const viewState = resolveMentorViewState({
		isLoading:
			customisationQuery.isLoading ||
			userLearningPathQuery.isLoading ||
			learningPathQuery.isLoading,
		isError,
		snapshot: isGenerating ? null : snapshot,
	})

	const isTutorialReady = viewState === 'empty' || viewState === 'ready'
	const tutorialAvailability = useMemo(
		() => ({
			hasQuestionnaire: viewState === 'empty',
			hasPath:
				viewState === 'ready' && Boolean(snapshot) && snapshot!.path.length > 0,
			hasRecommendation:
				viewState === 'ready' && Boolean(snapshot?.recommendation),
			hasRegenerate: viewState === 'ready' && Boolean(snapshot?.recommendation),
		}),
		[viewState, snapshot],
	)

	useEffect(() => {
		if (!isLlmGenerationStep) {
			setLlmGenerationMessageIndex(0)
			return undefined
		}
		const INTERVAL_PERIOD = 5_000
		const interval = window.setInterval(() => {
			setLlmGenerationMessageIndex(
				index => (index + 1) % LLM_GENERATION_MESSAGES.length,
			)
		}, INTERVAL_PERIOD)

		return () => window.clearInterval(interval)
	}, [isLlmGenerationStep])

	useEffect(() => {
		if (!socket) return

		const handleMessage = async (event: MessageEvent) => {
			try {
				const outer = JSON.parse(event.data)
				if (typeof outer.response !== 'string') return

				const inner = JSON.parse(outer.response)
				if (inner.type !== 'learning_path_progress') return
				setProgressStep(inner.step)
				const status = PROGRESS_STEP_TO_STATUS[inner.step]
				if (status) setProgressStatus(status)
				if (status === 'GENERATING_LEARNING_PATH' && inner.phase === 'started') {
					setLlmGenerationStartedAt(Date.now())
				}
				// Update the live progress message for every step/phase.
				if (inner.message) {
					setGeneratingMessage(inner.message)
				}
				// The final completion signals that the path has been applied
				// and the GET endpoint will now return fresh nodes.
				if (
					inner.step === 'apply_learning_path_update' &&
					inner.phase === 'completed'
				) {
					setGeneratingMessage(null)
					setProgressStep(null)
					setProgressStatus('ACTIVE')
					setLlmGenerationStartedAt(null)
					try {
						// Keep the full-screen generating view mounted until fresh
						// data is actually in the cache — clearing
						// isGenerationRequested before this resolves would let the
						// ready view render one frame with stale (old-path or
						// empty) data first.
						await userLearningPathQuery.refetch()
						await learningPathQuery.refetch()
					} finally {
						setIsGenerationRequested(false)
						generationDispatchedAtRef.current = null
						// Reset the progress state back to initial now, regardless of
						// which flow triggered this completion, so the next
						// regeneration (of either kind) starts from a clean 0 instead
						// of visibly dropping from this run's 100% back down to 0.
						setProgressStatus(null)
					}
				}
			} catch {
				// Defensive: ignore any unexpected parse failures.
			}
		}

		socket.addEventListener('message', handleMessage)
		return () => socket.removeEventListener('message', handleMessage)
	}, [socket, learningPathQuery, userLearningPathQuery])

	// Fallback for the WS completion message above never arriving at all
	// (e.g. a dropped socket, or the worker crashing outright) — without
	// this, isGenerationRequested has no other way to clear and the
	// generating panel would stay stuck forever with no way to retry short
	// of a reload. Poll on a plain interval rather than depending on any
	// particular render happening to recompute staleness, and compare
	// against this dispatch's own timestamp (not the query cache's
	// updated_at) so a request can't read as already-stale the moment it's
	// sent. Mirrors IN_FLIGHT_STALENESS_MS, the same threshold the backend
	// uses to allow a fresh request.
	useEffect(() => {
		if (!isGenerationRequested) return undefined
		const STALENESS_CHECK_PERIOD_MS = 30 * 1000
		const interval = window.setInterval(() => {
			const dispatchedAt = generationDispatchedAtRef.current
			if (dispatchedAt === null) return
			if (Date.now() - dispatchedAt < IN_FLIGHT_STALENESS_MS) return
			generationDispatchedAtRef.current = null
			setIsGenerationRequested(false)
			setGeneratingMessage(null)
			setProgressStep(null)
			setProgressStatus(null)
			setLlmGenerationStartedAt(null)
			setRegenerateAlert({
				open: true,
				severity: 'error',
				message:
					'This is taking longer than expected. Please try again in a moment.',
			})
		}, STALENESS_CHECK_PERIOD_MS)
		return () => window.clearInterval(interval)
	}, [isGenerationRequested])

	// Drop any selected ids that no longer correspond to a node in the current
	// path (e.g. a background regeneration replaced them while the panel was
	// open), so the selection count and handleRegenerateSelected's uuid list
	// never disagree with each other.
	useEffect(() => {
		setSelectedNodeIds(prev => {
			if (prev.size === 0) return prev
			const validIds = new Set(pathNodes.map(node => node.id))
			const next = new Set<string>()
			prev.forEach(id => {
				if (validIds.has(id)) next.add(id)
			})
			return next.size === prev.size ? prev : next
		})
	}, [pathNodes])

	const handleToggle = useCallback(() => {
		if (!authenticated) return
		setIsOpen(prev => !prev)
	}, [authenticated])
	const handleClose = useCallback(() => {
		setIsOpen(false)
		setSelectedNodeIds(new Set())
	}, [])
	const openPreferences = useCallback(() => setMode('preferences'), [])
	const openSummary = useCallback(() => setMode('summary'), [])
	const returnToView = useCallback(() => setMode('view'), [])
	const toggleNodeSelection = useCallback((id: string) => {
		setSelectedNodeIds(prev => {
			const next = new Set(prev)
			if (next.has(id)) {
				next.delete(id)
			} else {
				next.add(id)
			}
			return next
		})
	}, [])

	// Completing the Q&A: save (create or patch) the customisation then
	// trigger learning-path generation via the Celery worker.
	const handleComplete = useCallback(
		async (answers: UserCustomisation) => {
			const payload = toBackendCustomisation(answers)
			const isUpdate = hasExistingCustomisation

			try {
				await saveCustomisation.mutateAsync({ payload, isUpdate })
				setGeneratingMessage(null)
				setProgressStep(null)
				setProgressStatus('PENDING')
				setLlmGenerationStartedAt(null)
				generationDispatchedAtRef.current = Date.now()
				setIsGenerationRequested(true)
				await requestLearningPath.mutateAsync({ clear: isGenerationStale })
				void userLearningPathQuery.refetch()
				// The WS listener will update generatingMessage, clear
				// isGenerating, and re-fetch once complete.
			} catch (error) {
				generationDispatchedAtRef.current = null
				setIsGenerationRequested(false)
				setGeneratingMessage(null)
				setProgressStep(null)
				setProgressStatus(null)
				setLlmGenerationStartedAt(null)
				setRegenerateAlert({
					open: true,
					severity: 'error',
					message: getRegenerateErrorMessage(error),
				})
			}
			setMode('view')
		},
		[
			hasExistingCustomisation,
			isGenerationStale,
			saveCustomisation,
			requestLearningPath,
			userLearningPathQuery,
		],
	)

	// Regenerate reusing the currently-saved preferences. Requires an existing,
	// valid customisation — the backend rejects a request without one — so
	// without one this falls back to the setup flow instead of firing a
	// request that's guaranteed to fail.
	const handleRegenerate = useCallback(async () => {
		if (!canRegenerate) return
		if (!hasExistingCustomisation) {
			setMode('preferences')
			return
		}
		try {
			setGeneratingMessage(null)
			setProgressStep(null)
			setProgressStatus('PENDING')
			setLlmGenerationStartedAt(null)
			generationDispatchedAtRef.current = Date.now()
			setIsGenerationRequested(true)
			await requestLearningPath.mutateAsync({ clear: true })
			void userLearningPathQuery.refetch()
		} catch (error) {
			generationDispatchedAtRef.current = null
			setIsGenerationRequested(false)
			setGeneratingMessage(null)
			setProgressStep(null)
			setProgressStatus(null)
			setLlmGenerationStartedAt(null)
			setRegenerateAlert({
				open: true,
				severity: 'error',
				message: getRegenerateErrorMessage(error),
			})
		}
		setMode('view')
	}, [
		canRegenerate,
		hasExistingCustomisation,
		requestLearningPath,
		userLearningPathQuery,
	])

	// Regenerate only the picked nodes, reusing the same generating panel as
	// handleRegenerate (isGenerationRequested) rather than a separate
	// per-node spinner — the ready card unmounts and the panel shows the
	// normal "Creating Your Learning Path" progress until the update lands.
	const handleRegenerateSelected = useCallback(async () => {
		if (!canRegenerate) return
		if (selectedTypeUuids.length === 0) return
		if (!hasExistingCustomisation) {
			setMode('preferences')
			return
		}
		setSelectedNodeIds(new Set())
		setGeneratingMessage(null)
		setProgressStep(null)
		setProgressStatus('PENDING')
		setLlmGenerationStartedAt(null)
		generationDispatchedAtRef.current = Date.now()
		setIsGenerationRequested(true)
		try {
			await requestLearningPath.mutateAsync({ uuid: selectedTypeUuids })
			void userLearningPathQuery.refetch()
		} catch (error) {
			// Dispatch itself failed (e.g. rejected as already-in-progress), so
			// nothing is actually regenerating — return to the ready view
			// immediately rather than waiting on a completion signal that will
			// never come.
			generationDispatchedAtRef.current = null
			setIsGenerationRequested(false)
			setGeneratingMessage(null)
			setProgressStep(null)
			setProgressStatus(null)
			setLlmGenerationStartedAt(null)
			setRegenerateAlert({
				open: true,
				severity: 'error',
				message: getRegenerateErrorMessage(error),
			})
		}
	}, [
		canRegenerate,
		hasExistingCustomisation,
		selectedTypeUuids,
		requestLearningPath,
		userLearningPathQuery,
	])

	// The recommendation card's regenerate control is dual-purpose: with
	// nothing picked it opens the preferences summary (the existing full
	// "clear" flow); with nodes picked it fires the batched request directly.
	// Without an existing customisation neither request can succeed, so it
	// opens the setup flow instead.
	const handleRegenerateControlClick = useCallback(() => {
		if (!canRegenerate) return
		if (!hasExistingCustomisation) {
			openPreferences()
		} else if (selectedNodeIds.size > 0) {
			void handleRegenerateSelected()
		} else {
			openSummary()
		}
	}, [
		canRegenerate,
		hasExistingCustomisation,
		selectedNodeIds,
		handleRegenerateSelected,
		openSummary,
		openPreferences,
	])

	const handleOutsideClick = useCallback((event: MouseEvent) => {
		const target = event.target
		if (!(target instanceof Node)) return
		if (tutorialActiveRef.current) return
		// The regenerate alert and tooltip poppers (e.g. the "Subscribe" link in
		// the blocked-regenerate tooltip) are portalled to document.body, so
		// they're outside both containerRef and panelRef; without this they'd
		// be treated as an "outside" click and close the panel when clicked.
		if (
			target instanceof Element &&
			target.closest('.MuiModal-root, .MuiSnackbar-root, .MuiTooltip-popper')
		)
			return
		if (containerRef.current?.contains(target)) return
		if (panelRef.current?.contains(target)) return
		setIsOpen(false)
	}, [])

	useEffect(() => {
		if (!isOpen) return undefined

		const previouslyFocused =
			document.activeElement instanceof HTMLElement ? document.activeElement : null
		panelRef.current?.focus()

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return
			if (tutorialActiveRef.current) return
			if (document.querySelector('.MuiModal-root')) return
			setIsOpen(false)
		}
		document.addEventListener('mousedown', handleOutsideClick)
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick)
			document.removeEventListener('keydown', handleKeyDown)
			previouslyFocused?.focus()
		}
	}, [isOpen, handleOutsideClick])

	const greetingName =
		profile.firstName?.trim() || profile.username?.trim() || 'there'
	const greeting = `Hey ${greetingName}, let's build a personalised learning pathway for you.`

	return (
		<>
			{createPortal(
				// The Header's own root is `position: fixed; overflow: hidden` (a
				// slim bar), which clips any `position: fixed` descendant to its
				// own bounding box — including this Snackbar. Portalling it to
				// document.body (like the panel below) escapes that clipping so
				// it's actually visible.
				<SecDimAlert
					isAlertShown={regenerateAlert.open}
					setAlertShown={open =>
						setRegenerateAlert(currentAlert => ({ ...currentAlert, open }))
					}
					severity={regenerateAlert.severity}
					alertMessage={regenerateAlert.message}
					// Above the Header's own z-index (1500), since the alert
					// renders near the top of the viewport, in the same region.
					sx={{ zIndex: 1600 }}
				/>,
				document.body,
			)}
			<div ref={containerRef} data-tour='dr-secdim-trigger'>
				<Tooltip
					title={
						authenticated ? '' : 'Please sign in to get your customised learning path'
					}
					placement='bottom'
					disableHoverListener={Boolean(authenticated)}
					disableFocusListener={Boolean(authenticated)}
					disableTouchListener={Boolean(authenticated)}
				>
					<Styled.TriggerButton
						type='button'
						aria-label='Open Dr. SecDim'
						aria-haspopup='dialog'
						aria-expanded={authenticated ? isOpen : false}
						aria-controls='dr-secdim-panel'
						aria-disabled={authenticated ? undefined : 'true'}
						onClick={handleToggle}
					>
						<Styled.BrainIcon src={mlIcon} alt='' />
					</Styled.TriggerButton>
				</Tooltip>
			</div>
			{createPortal(
				<DrSecDimPanel
					ref={panelRef}
					isOpen={isOpen}
					viewState={viewState}
					mode={mode}
					mentor={snapshot}
					customisation={customisation}
					greeting={greeting}
					isGenerating={isGenerating}
					generatingMessage={displayedGeneratingMessage}
					generationProgress={generationProgress}
					onOpenPreferences={openPreferences}
					onCompletePreferences={handleComplete}
					onReturnToView={returnToView}
					onRegenerate={handleRegenerate}
					onClose={handleClose}
					selectedNodeIds={selectedNodeIds}
					onToggleNode={toggleNodeSelection}
					selectedCount={selectedNodeIds.size}
					isRegenerateBusy={isGenerating}
					canRegenerate={canRegenerate}
					onRegenerateControlClick={handleRegenerateControlClick}
					onRestartTutorial={handleRestartTutorial}
				/>,
				document.body,
			)}
			<DrSecDimTutorial
				ref={tutorialRef}
				authenticated={Boolean(authenticated)}
				profileGuid={profile.guid}
				isReady={isTutorialReady}
				isPanelOpen={isOpen}
				availability={tutorialAvailability}
				hasCustomisation={hasCustomisation(customisation)}
				onSetPanelOpen={setPanelOpenForTutorial}
				onActiveChange={handleTutorialActiveChange}
			/>
		</>
	)
}

export default DrSecDim
